import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useBrand } from "../lib/brand";
import { Check, ArrowRight, Scissors } from "lucide-react";

const STEPS = [
  { key: "org", title: "Your company", desc: "The account that owns your brand(s)." },
  { key: "brand", title: "Your first brand", desc: "A workspace with isolated collections, files, orders." },
  { key: "done", title: "Ready", desc: "Start using MMS HUB CENTER." },
];

export default function OnboardingPage() {
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const { refresh: refreshBrands, select } = useBrand();
  const [step, setStep] = useState("org");
  const [org, setOrg] = useState({ name: "", billing_email: user?.email || "", phone: "", country: "" });
  const [brand, setBrand] = useState({ name: "", description: "", website: "", instagram: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [createdBrandId, setCreatedBrandId] = useState(null);
  const idx = STEPS.findIndex(s => s.key === step);

  const submitOrg = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try {
      if (!user?.organization_id) {
        await api.post("/organizations", org);
        await refresh();
      }
      setStep("brand");
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    setLoading(false);
  };

  const submitBrand = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try {
      const { data } = await api.post("/brands", brand);
      setCreatedBrandId(data.id);
      await select(data.id);
      await refreshBrands();
      setStep("done");
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[var(--bg)] text-[#0A0A0A] p-8" data-testid="onboarding-page">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-3 mb-10">
          <Scissors size={20} className="text-[#0A0A0A]" strokeWidth={1.5}/>
          <div className="font-serif text-xl tracking-widest">MMS HUB CENTER</div>
        </div>

        <div className="flex items-center gap-2 mb-10">
          {STEPS.map((s, i) => (
            <div key={s.key} className="flex items-center flex-1">
              <div className={`w-8 h-8 flex items-center justify-center border ${i <= idx ? "border-[#0A0A0A] text-[#0A0A0A]" : "border-[#E5E5E5] text-[#737373]"}`}>
                {i < idx ? <Check size={14}/> : <span className="text-xs">{i+1}</span>}
              </div>
              {i < STEPS.length - 1 && <div className={`flex-1 h-px mx-2 ${i < idx ? "bg-[#0A0A0A]" : "bg-[#E5E5E5]"}`}/>}
            </div>
          ))}
        </div>

        <div className="label-caps text-[#0A0A0A] mb-3">Setup — step {idx+1} of {STEPS.length}</div>
        <h1 className="font-serif text-4xl font-light mb-2">{STEPS[idx].title}</h1>
        <p className="text-[#525252] text-sm mb-8">{STEPS[idx].desc}</p>

        {step === "org" && (
          <form onSubmit={submitOrg} className="card-dark p-6 space-y-4" data-testid="onboarding-org-form">
            <div><label className="label-caps block mb-2">Company name</label>
              <input required autoFocus className="input-dark" data-testid="onb-org-name" value={org.name} onChange={(e)=>setOrg({...org,name:e.target.value})}/></div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="label-caps block mb-2">Billing email</label>
                <input type="email" className="input-dark" value={org.billing_email} onChange={(e)=>setOrg({...org,billing_email:e.target.value})}/></div>
              <div><label className="label-caps block mb-2">Phone</label>
                <input className="input-dark" value={org.phone} onChange={(e)=>setOrg({...org,phone:e.target.value})}/></div>
            </div>
            <div><label className="label-caps block mb-2">Country</label>
              <input className="input-dark" value={org.country} onChange={(e)=>setOrg({...org,country:e.target.value})}/></div>
            {error && <div className="text-red-600 text-sm">{error}</div>}
            <button disabled={loading} className="btn-gold w-full inline-flex items-center justify-center gap-2" data-testid="onb-org-submit">
              {loading ? "Saving…" : "Continue"} <ArrowRight size={14}/>
            </button>
            {user?.organization_id && (
              <button type="button" onClick={()=>setStep("brand")} className="btn-ghost w-full">Skip — I already have an organization</button>
            )}
          </form>
        )}

        {step === "brand" && (
          <form onSubmit={submitBrand} className="card-dark p-6 space-y-4" data-testid="onboarding-brand-form">
            <div><label className="label-caps block mb-2">Brand name</label>
              <input required autoFocus className="input-dark" data-testid="onb-brand-name" value={brand.name} onChange={(e)=>setBrand({...brand,name:e.target.value})}/></div>
            <div><label className="label-caps block mb-2">Description</label>
              <textarea rows={2} className="input-dark" value={brand.description} onChange={(e)=>setBrand({...brand,description:e.target.value})}/></div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="label-caps block mb-2">Website</label>
                <input className="input-dark" value={brand.website} onChange={(e)=>setBrand({...brand,website:e.target.value})}/></div>
              <div><label className="label-caps block mb-2">Instagram</label>
                <input className="input-dark" value={brand.instagram} onChange={(e)=>setBrand({...brand,instagram:e.target.value})}/></div>
            </div>
            {error && <div className="text-red-600 text-sm">{error}</div>}
            <button disabled={loading} className="btn-gold w-full inline-flex items-center justify-center gap-2" data-testid="onb-brand-submit">
              {loading ? "Creating…" : "Create brand & enter"} <ArrowRight size={14}/>
            </button>
          </form>
        )}

        {step === "done" && (
          <div className="card-dark p-8 text-center space-y-6">
            <div className="w-14 h-14 mx-auto flex items-center justify-center border border-[#0A0A0A] text-[#0A0A0A]"><Check size={22}/></div>
            <div className="font-serif text-3xl">You&apos;re all set.</div>
            <p className="text-[#525252] text-sm">Your workspace is ready. Head to the dashboard to create your first collection.</p>
            <div className="flex flex-col md:flex-row gap-3">
              <button onClick={()=>nav("/dashboard")} className="btn-gold flex-1" data-testid="onb-goto-dashboard">Go to dashboard</button>
              <button onClick={()=>nav("/settings/brand")} className="btn-ghost flex-1">Customize brand theme</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
